/**
 * Une couleur par affaire, pour le planning des ressources vu toutes affaires confondues.
 *
 * *Demande de Chris, 21/09/2026 : « dans le planning des ressources il faut pouvoir avoir une
 * vue de toutes les affaires et que toutes les affaires soient différenciées par couleur ».*
 *
 * ## Pourquoi la couleur ne se stocke pas
 *
 * Aucune colonne, aucun écran d'administration, aucune migration : la couleur se **déduit du
 * numéro d'affaire**. Personne n'a demandé à la choisir, et un attribut à saisir serait une
 * fonction qui arrive muette tant que personne ne l'a remplie — le défaut que la 059 a dû
 * reprendre. Si le métier veut un jour choisir, la colonne s'ajoutera et cette fonction
 * deviendra le repli.
 *
 * ## Déduite, donc STABLE dans le temps
 *
 * Elle vient de l'APO et de rien d'autre : APO260128 garde sa couleur demain, la semaine
 * prochaine, et quelle que soit la période affichée. Une couleur tirée du **rang** dans la
 * liste aurait été plus simple et fausse à la première affaire importée — toutes les autres
 * auraient changé de teinte du jour au lendemain, sur un écran dont l'unique fonction est de
 * reconnaître une affaire à sa couleur.
 *
 * ## Et DISTINCTE entre les affaires présentes
 *
 * Une empreinte modulo la palette finit par cogner : deux affaires de la même teinte, sur le
 * seul écran où la teinte porte le sens. Les collisions sont donc résolues à la construction —
 * la seconde arrivée prend l'emplacement libre suivant. Deux conséquences à connaître :
 *
 *  - la distinction est garantie **tant qu'il y a moins d'affaires que de couleurs**. Au-delà,
 *    deux affaires partagent forcément une teinte, et `couleursDesAffaires` ne le cache pas :
 *    la légende affiche les deux, et c'est elle qui tranche ;
 *  - une affaire peut **changer de couleur** si une autre lui prend sa place naturelle. Ça ne
 *    se produit qu'entre affaires simultanément au plan, et la légende est toujours à l'écran.
 */

/**
 * Les teintes, assez pâles pour qu'un nombre reste lisible par-dessus.
 *
 * **Huit, et le compte n'est pas un choix esthétique** : c'est ce qui reste une fois écartées
 * les teintes qui disent déjà autre chose sur ce tableau. La palette est donc plus courte que
 * les listes usuelles, et c'est le prix d'un écran où la couleur porte le sens.
 *
 * Trois familles sont écartées, chacune pour un défaut mesuré :
 *
 *  - **le GRIS**, vu à l'écran le 21/09/2026 : sur ce tableau, le gris est la couleur du vide —
 *    en-têtes, ligne de pied, cases sans rien. L'affaire qui l'avait tirée se lisait comme une
 *    colonne inactive ;
 *  - **les ROSES ET ROUGES ORANGÉS**, vus le même jour : composés sur le blanc du tableau, ils
 *    tombent à moins de cinquante unités du rouge de « plusieurs affaires le même moment ».
 *    Le rose `227 119 194` en était à 48, le brun `140 86 75` à 18 — en `rgb()` ils n'ont rien
 *    à voir, à l'écran ils se confondent. C'est `ecartVu`, plus bas, qui le mesure, et un essai
 *    qui interdit d'en réintroduire un ;
 *  - **l'ORANGE franc**, trop proche du « sur un autre chantier » qu'il côtoie dans l'autre
 *    mode d'affichage.
 *
 * Le vert de « sur l'affaire regardée » reste disponible : il ne peut pas coexister avec ces
 * couleurs, puisqu'elles ne s'affichent que lorsqu'aucune affaire n'est choisie.
 *
 * Mesuré : **51 unités au minimum du rouge de partage, 27 entre deux teintes de la palette.**
 */
export const COULEURS_AFFAIRE: readonly string[] = [
  "rgb(31 119 180 / 0.38)",
  "rgb(23 190 207 / 0.42)",
  "rgb(44 160 44 / 0.36)",
  "rgb(148 103 189 / 0.55)",
  "rgb(188 189 34 / 0.50)",
  "rgb(255 187 0 / 0.55)",
  "rgb(0 150 136 / 0.40)",
  "rgb(121 85 72 / 0.55)",
];

/**
 * L'empreinte d'un numéro d'affaire.
 *
 * Une somme pondérée, pas un hachage cryptographique : on veut deux APO consécutifs — `APO260112`
 * et `APO260128` — dans des emplacements éloignés, et rien de plus. Écrite à la main plutôt
 * qu'importée, comme l'encodeur QR de bien-archiver : une dépendance pour douze lignes de calcul
 * est une dépendance de trop sur un produit qu'un repreneur devra relire.
 */
export function empreinte(apo: string): number {
  let valeur = 0;
  for (let i = 0; i < apo.length; i += 1) {
    // 31 est le multiplicateur usuel des empreintes de chaîne : il disperse les suffixes
    // proches, ce qui est exactement le cas d'une série d'APO qui ne diffèrent que par leurs
    // derniers chiffres. `>>> 0` garde la valeur positive sur 32 bits.
    valeur = (Math.imul(valeur, 31) + apo.charCodeAt(i)) >>> 0;
  }
  return valeur;
}

/**
 * La couleur de chaque affaire, déduite de son APO et rendue distincte des autres.
 *
 * L'ordre d'entrée décide qui garde son emplacement naturel en cas de collision : passer les
 * affaires dans l'ordre du plan — celui de l'APO — rend donc l'attribution reproductible d'un
 * rendu à l'autre.
 */
export function couleursDesAffaires(apos: readonly string[]): Map<string, string> {
  const couleurs = new Map<string, string>();
  const pris = new Set<number>();
  for (const apo of apos) {
    if (couleurs.has(apo)) continue;
    const naturel = empreinte(apo) % COULEURS_AFFAIRE.length;
    let emplacement = naturel;
    // On avance d'un cran tant que la place est prise, et on s'arrête après un tour complet :
    // au-delà, toutes les couleurs servent déjà et il n'y a plus rien à éviter.
    for (let essai = 0; essai < COULEURS_AFFAIRE.length && pris.has(emplacement); essai += 1) {
      emplacement = (emplacement + 1) % COULEURS_AFFAIRE.length;
    }
    pris.add(emplacement);
    couleurs.set(apo, COULEURS_AFFAIRE[emplacement] ?? COULEURS_AFFAIRE[0] ?? "transparent");
  }
  return couleurs;
}

/**
 * La couleur telle qu'elle se VOIT, composée sur le fond blanc du tableau.
 *
 * Deux teintes très différentes en `rgb()` peuvent se ressembler une fois posées à 30 %
 * d'opacité sur du blanc — c'est ce qui est arrivé le 21/09/2026 entre le rose d'une affaire et
 * le rouge de « plusieurs affaires le même moment ». Comparer les valeurs déclarées n'aurait
 * rien vu ; comparer ce que l'œil reçoit, si.
 */
export function surBlanc(couleur: string): readonly [number, number, number] {
  const trouve = /rgb\((\d+) (\d+) (\d+) \/ ([\d.]+)\)/u.exec(couleur);
  if (trouve === null) throw new Error(`Couleur illisible : « ${couleur} ».`);
  const [r, v, b, a] = trouve.slice(1).map(Number) as [number, number, number, number];
  return [255 - (255 - r) * a, 255 - (255 - v) * a, 255 - (255 - b) * a];
}

/** La distance entre deux couleurs vues, en composantes. Assez pour ce qu'on en fait. */
export function ecartVu(a: string, b: string): number {
  const [ar, av, ab] = surBlanc(a);
  const [br, bv, bb] = surBlanc(b);
  return Math.hypot(ar - br, av - bv, ab - bb);
}

/**
 * Les couleurs qui disent déjà autre chose sur ce tableau, et dont la palette doit s'éloigner.
 *
 * Elles sont recopiées de `style.css` — les seules valeurs dupliquées de ce module, et un essai
 * vérifie que la feuille de style les porte encore. Les lire depuis le CSS demanderait de le
 * parser ; les oublier ferait passer un essai vert sur une règle qui ne protège plus rien.
 */
export const COULEURS_DE_SENS: Readonly<Record<string, string>> = {
  /** « Plusieurs affaires le même moment » — le seul qui coexiste avec la palette. */
  partage: "rgb(179 38 30 / 0.28)",
  /** « Sur un autre chantier » : n'apparaît pas en vue toutes affaires, mais reste voisin. */
  ailleurs: "rgb(191 120 0 / 0.28)",
};
